import React, { useState, useEffect, useMemo } from 'react';
import ElectorateSelector from '../components/selectors/ElectorateSelector';
import BarChart from '../components/charts/BarChart';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import { getCachedData, setCachedData, CACHE_KEYS } from '../utils/cacheUtils';

const HomePage = ({ appData }) => {
  console.log('HomePage component rendering');
  console.log('appData:', appData ? 'present' : 'missing');
  
  const [selectedElectorate, setSelectedElectorate] = useState(null);
  const [similarElectorates, setSimilarElectorates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [aggregateData, setAggregateData] = useState(() => {
    // Initialize with cached data if available or default values
    const cached = getCachedData(CACHE_KEYS.AGGREGATE_DATA);
    return cached || {
      byAgency: [],
      byYear: [],
      totalFunding: 0
    };
  });
  
  // Extract data safely using useMemo to avoid re-creating objects on every render
  const electorates = useMemo(() => appData?.electorates || [], [appData]);
  const states = useMemo(() => appData?.states || {}, [appData]);
  const navigate = appData?.navigate;
  
  // Function to pre-process electorate data
  const processElectorateData = (data, filename) => {
    if (!data) return null;
    
    try {
      // Extract and return only the data we need for aggregation
      // This reduces memory usage during processing
      return {
        totalValue: data.totalValue || 0,
        byAgency: data.byAgency || {},
        byYear: data.byYear || {}
      };
    } catch (err) {
      console.warn(`Error processing data for ${filename}:`, err);
      return null;
    }
  };
  
  // Chunked processing of electorate data
  const processInChunks = async (allElectorates, chunkSize, callback) => {
    const results = [];
    for (let i = 0; i < allElectorates.length; i += chunkSize) {
      const chunk = allElectorates.slice(i, i + chunkSize);
      await callback(chunk, i, results);
      // Allow UI to update
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    return results;
  };
  
  // Load aggregate data for all electorates with optimized approach
  useEffect(() => {
    const loadAggregateData = async () => {
      if (!electorates || electorates.length === 0) return;
      
      // Check for cached data first to avoid expensive computation
      const cachedData = getCachedData(CACHE_KEYS.AGGREGATE_DATA);
      if (cachedData) {
        console.log('Using cached aggregate data');
        setAggregateData(cachedData);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Prepare containers for aggregate data
        const agencyTotals = {};
        const yearlyTotals = {};
        let grandTotal = 0;

        // Get the base URL for data files
        const isNetlify = window.location.hostname.includes('netlify.app') || 
                         window.location.hostname === 'watershed-apps.co';
        const baseUrl = isNetlify ? '' : (process.env.PUBLIC_URL || '');
        
        // Use web workers for processing if available
        const useWorkers = typeof Worker !== 'undefined' && window.navigator.hardwareConcurrency > 1;
        
        // Increased batch size for better performance
        const BATCH_SIZE = useWorkers ? 20 : 10;
        
        // Start with a smaller initial batch to show data quickly
        const INITIAL_BATCH_SIZE = 5;
        
        // Process initial batch to quickly show some data
        if (electorates.length > 0) {
          const initialElectorates = electorates.slice(0, INITIAL_BATCH_SIZE);
          
          // Create promises for initial batch
          const initialPromises = initialElectorates.map(async (electorate) => {
            try {
              const filename = electorate.name.replace(/\s+/g, '_');
              const dataUrl = `${baseUrl}/data/electorates/${filename}.json`;
              const response = await fetch(dataUrl);
              
              if (!response.ok) {
                console.warn(`Failed to load initial data for ${electorate.name}`);
                return null;
              }
              
              const data = await response.json();
              return processElectorateData(data, filename);
            } catch (err) {
              console.warn(`Error loading initial data for ${electorate.name}:`, err);
              return null;
            }
          });
          
          // Process initial results
          const initialResults = await Promise.all(initialPromises);
          
          // Update with initial data
          let initialTotal = 0;
          const initialAgencyTotals = {};
          const initialYearlyTotals = {};
          
          initialResults.forEach(data => {
            if (!data) return;
            
            // Add to totals
            initialTotal += data.totalValue || 0;
            
            // Aggregate agency data
            Object.entries(data.byAgency || {}).forEach(([agency, value]) => {
              initialAgencyTotals[agency] = (initialAgencyTotals[agency] || 0) + value;
            });
            
            // Aggregate yearly data
            Object.entries(data.byYear || {}).forEach(([year, value]) => {
              initialYearlyTotals[year] = (initialYearlyTotals[year] || 0) + value;
            });
          });
          
          // Format initial agency data
          const initialAgencyData = Object.entries(initialAgencyTotals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([name, value]) => ({ name, value }));
          
          // Format initial yearly data
          const initialYearlyData = Object.entries(initialYearlyTotals)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([year, value]) => ({ year, value }));
          
          // Update with initial results
          setAggregateData({
            byAgency: initialAgencyData,
            byYear: initialYearlyData,
            totalFunding: initialTotal
          });
        }
        
        // Process the rest of the data in chunks
        await processInChunks(
          electorates.slice(INITIAL_BATCH_SIZE),
          BATCH_SIZE,
          async (chunk, index, results) => {
            const chunkStart = INITIAL_BATCH_SIZE + index;
            console.log(`Processing batch: electorates ${chunkStart+1}-${chunkStart+chunk.length}`);
            
            // Create promises for current chunk
            const chunkPromises = chunk.map(async (electorate) => {
              try {
                const filename = electorate.name.replace(/\s+/g, '_');
                const dataUrl = `${baseUrl}/data/electorates/${filename}.json`;
                
                // Use fetch with timeout to avoid hanging requests
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                
                try {
                  const response = await fetch(dataUrl, { signal: controller.signal });
                  clearTimeout(timeoutId);
                  
                  if (!response.ok) {
                    console.warn(`Failed to load data for ${electorate.name}`);
                    return null;
                  }
                  
                  const data = await response.json();
                  return processElectorateData(data, filename);
                } catch (err) {
                  clearTimeout(timeoutId);
                  if (err.name === 'AbortError') {
                    console.warn(`Request timed out for ${electorate.name}`);
                  } else {
                    console.warn(`Error loading data for ${electorate.name}:`, err);
                  }
                  return null;
                }
              } catch (err) {
                console.warn(`Error processing ${electorate.name}:`, err);
                return null;
              }
            });
            
            // Process chunk results
            const chunkResults = await Promise.all(chunkPromises);
            results.push(...chunkResults.filter(Boolean));
            
            // Update running totals
            chunkResults.forEach(data => {
              if (!data) return;
              
              // Add to total
              grandTotal += data.totalValue || 0;
              
              // Aggregate agency data
              Object.entries(data.byAgency || {}).forEach(([agency, value]) => {
                agencyTotals[agency] = (agencyTotals[agency] || 0) + value;
              });
              
              // Aggregate yearly data
              Object.entries(data.byYear || {}).forEach(([year, value]) => {
                yearlyTotals[year] = (yearlyTotals[year] || 0) + value;
              });
            });
            
            // Format current agency data
            const currentAgencyData = Object.entries(agencyTotals)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([name, value]) => ({ name, value }));
            
            // Format current yearly data
            const currentYearlyData = Object.entries(yearlyTotals)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([year, value]) => ({ year, value }));
            
            // Update with incremental results
            setAggregateData({
              byAgency: currentAgencyData,
              byYear: currentYearlyData,
              totalFunding: grandTotal
            });
          }
        );
        
        // Final data processing
        // Format agency data for pie chart (top 6)
        const agencyData = Object.entries(agencyTotals)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([name, value]) => ({ name, value }));
        
        // Format yearly data for bar chart
        const yearlyData = Object.entries(yearlyTotals)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([year, value]) => ({ year, value }));
        
        // Final result
        const finalData = {
          byAgency: agencyData,
          byYear: yearlyData,
          totalFunding: grandTotal
        };
        
        setAggregateData(finalData);
        
        // Cache the final results
        setCachedData(CACHE_KEYS.AGGREGATE_DATA, finalData);
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading aggregate data:', err);
        setIsLoading(false);
      }
    };
    
    loadAggregateData();
  }, [electorates]);
  
  // Find similar electorates based on size and area
  useEffect(() => {
    const findSimilarElectorates = async () => {
      if (!selectedElectorate || !appData) return;
      
      try {
        setIsLoading(true);
        
        // Ensure we have state data
        const selectedState = selectedElectorate.state;
        if (!selectedState) {
          throw new Error('Selected electorate has no state information');
        }
        
        // Find similar electorates using the data from appData
        console.log(`Finding similar electorates for ${selectedElectorate.name} in ${selectedState}`);
        
        // First check for predefined similar electorates from data
        if (appData.similarityData && appData.similarityData[selectedElectorate.name]) {
          const similarNames = appData.similarityData[selectedElectorate.name];
          console.log(`Found predefined similar electorates for ${selectedElectorate.name}:`, similarNames);
          
          // Get electorate objects from names
          const predefinedSimilar = similarNames
            .map(name => electorates.find(e => e.name === name))
            .filter(Boolean); // Remove any not found
          
          if (predefinedSimilar.length > 0) {
            console.log(`Using ${predefinedSimilar.length} predefined similar electorates`);
            setSimilarElectorates(predefinedSimilar);
            setIsLoading(false);
            return;
          }
          
          console.log('No valid predefined similar electorates found, falling back to calculation');
        }
        
        // If no predefined similar electorates, calculate based on metrics
        const similar = electorates
          .filter(e => 
            e.name !== selectedElectorate.name && 
            e.state === selectedState &&
            e.area && selectedElectorate.area)
          .map(e => ({
            ...e,
            // Calculate similarity score based on area, population, and funding differences
            similarityScore: calculateSimilarityScore(selectedElectorate, e)
          }))
          .sort((a, b) => a.similarityScore - b.similarityScore) // Lower is more similar
          .slice(0, 5); // Get top 5 similar
        
        if (similar.length === 0) {
          // If no electorates in same state, try with any state
          const anySimilar = electorates
            .filter(e => 
              e.name !== selectedElectorate.name && 
              e.area && selectedElectorate.area)
            .map(e => ({
              ...e,
              similarityScore: calculateSimilarityScore(selectedElectorate, e)
            }))
            .sort((a, b) => a.similarityScore - b.similarityScore)
            .slice(0, 5);
          
          setSimilarElectorates(anySimilar);
        } else {
          setSimilarElectorates(similar);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error finding similar electorates:', err);
        setSimilarElectorates([]);
        setIsLoading(false);
      }
    };
    
    findSimilarElectorates();
  }, [selectedElectorate, appData, electorates]);
  
  // Helper function to calculate similarity score
  const calculateSimilarityScore = (a, b) => {
    // Normalize values between 0-1 where lower is more similar
    const areaDiff = Math.abs(a.area - b.area) / Math.max(a.area, b.area);
    const popDiff = a.population && b.population 
      ? Math.abs(a.population - b.population) / Math.max(a.population, b.population)
      : 1;
    const fundingDiff = Math.abs(a.totalValue - b.totalValue) / Math.max(a.totalValue, b.totalValue);
    
    // Weight factors based on importance (can be adjusted)
    const areaWeight = 0.3;
    const popWeight = 0.4;
    const fundingWeight = 0.3;
    
    // Calculate weighted score
    return (areaDiff * areaWeight) + (popDiff * popWeight) + (fundingDiff * fundingWeight);
  };
  
  // Early return check after all hooks are called
  if (!appData) {
    console.error('appData is undefined or null in HomePage');
    return <div className="text-red-400 p-4 font-inter">Error: Application data not available</div>;
  }
  
  console.log('HomePage extracted data:');
  console.log('- electorates:', electorates ? `${electorates.length} items` : 'missing');
  console.log('- states:', states ? `${Object.keys(states).length} items` : 'missing');
  console.log('- navigate function:', !!navigate);
  
  // Handle electorate selection
  const handleElectorateSelect = (electorateName) => {
    console.log('Selecting electorate:', electorateName);
    
    // Find the electorate object
    const electorate = electorates.find(e => e.name === electorateName);
    
    if (electorate) {
      setSelectedElectorate(electorate);
    } else {
      console.warn(`Electorate not found: ${electorateName}`);
    }
    
    if (navigate) {
      navigate(`/electorate/${electorateName}`);
    } else {
      console.error('Navigate function not available');
    }
  };
  
  // Handle comparison of selected electorates
  const handleCompareElectorates = (electorateNames) => {
    console.log('Comparing electorates:', electorateNames);
    
    if (electorateNames.length < 2) {
      console.warn('Need at least 2 electorates to compare');
      return;
    }
    
    if (navigate) {
      navigate(`/compare/${electorateNames.join(',')}`);
    } else {
      console.error('Navigate function not available');
    }
  };
  
  // Automatically compare with similar electorates
  const handleCompareSimilar = () => {
    if (!selectedElectorate || similarElectorates.length === 0) return;
    
    const electorateNames = [
      selectedElectorate.name,
      ...similarElectorates.slice(0, 4).map(e => e.name)
    ];
    
    handleCompareElectorates(electorateNames);
  };
  
  return (
    <div className="space-y-8">
      <ElectorateSelector 
        electorates={electorates} 
        states={states} 
        onElectorateSelect={handleElectorateSelect}
        onCompareElectorates={handleCompareElectorates}
      />
      
      {isLoading && selectedElectorate && (
        <div className="my-4 text-center text-gray-400 font-inter">
          <p>Loading similar electorates data...</p>
        </div>
      )}
      
      {selectedElectorate && similarElectorates.length > 0 && (
        <div className="mb-6 bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 className="text-xl font-bold font-sora text-white">Similar Electorates to {selectedElectorate.name}</h2>
            <button
              onClick={handleCompareSimilar}
              className="px-5 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-600 transition-colors font-inter shadow-md"
            >
              Compare All Similar
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-700 text-gray-300 font-inter">
                  <th className="py-3 px-4 text-left font-semibold">Electorate</th>
                  <th className="py-3 px-4 text-left font-semibold">State</th>
                  <th className="py-3 px-4 text-right font-semibold">Total Value</th>
                  <th className="py-3 px-4 text-right font-semibold">Area (km²)</th>
                  <th className="py-3 px-4 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="font-inter">
                {similarElectorates.map(electorate => (
                  <tr 
                    key={electorate.name} 
                    className="border-b border-gray-700 hover:bg-gray-700 transition-colors"
                  >
                    <td className="py-3 px-4 text-blue-300">{electorate.name}</td>
                    <td className="py-3 px-4 text-gray-300">{electorate.state}</td>
                    <td className="py-3 px-4 text-right text-green-300">${electorate.totalValue.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-gray-300">{electorate.area.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">
                      <button 
                        onClick={() => navigate(`/electorate/${electorate.name}`)}
                        className="text-blue-400 hover:text-blue-300 mx-2 transition-colors"
                      >
                        View
                      </button>
                      <button 
                        onClick={() => handleCompareElectorates([selectedElectorate.name, electorate.name])}
                        className="text-purple-400 hover:text-purple-300 mx-2 transition-colors"
                      >
                        Compare
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 font-sora text-white">
            {selectedElectorate ? 'Total Grants Comparison' : 'Funding by Year (All Electorates)'}
          </h2>
          <div className="h-96">
            {selectedElectorate ? (
              <BarChart 
                electorate={selectedElectorate} 
                similarElectorates={similarElectorates} 
              />
            ) : (
              <>
                {aggregateData.byYear.length > 0 ? (
                  <div className="h-full flex flex-col">
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-full h-full" style={{ maxHeight: "100%" }}>
                        <LineChart data={aggregateData.byYear} />
                      </div>
                    </div>
                    <div className="mt-4 text-center text-sm text-gray-400 font-inter">
                      Total funding over time across all electorates 
                      {!isLoading && aggregateData.totalFunding > 0 && (
                        <span> - ${aggregateData.totalFunding.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-gray-750 rounded-lg flex items-center justify-center h-full text-gray-400 font-inter">
                    <div className="text-center">
                      <p className="mb-2">Loading aggregate funding data...</p>
                      <p className="text-sm">This may take a moment to compile data from all electorates.</p>
                      {isLoading && (
                        <div className="mt-4 flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className="bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 font-sora text-white">
            {selectedElectorate ? 'Agency Breakdown' : 'Agency Funding Distribution (All Electorates)'}
          </h2>
          <div className="h-96">
            {selectedElectorate ? (
              <div className="h-full flex items-center justify-center text-gray-400 font-inter">
                <div className="text-center">
                  <p className="mb-4">Select "View" to see detailed agency breakdowns</p>
                  <button
                    onClick={() => navigate(`/electorate/${selectedElectorate.name}`)}
                    className="px-5 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-600 transition-colors font-inter shadow-md"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ) : (
              <>
                {aggregateData.byAgency.length > 0 ? (
                  <div className="h-full flex flex-col">
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-full h-full" style={{ maxHeight: "100%" }}>
                        <PieChart 
                          data={aggregateData.byAgency} 
                          useMinimalTooltip={true}
                          options={{
                            plugins: {
                              legend: {
                                display: true,
                                position: 'right',
                                labels: {
                                  font: {
                                    size: 12,
                                    weight: 'medium'
                                  },
                                  padding: 6,
                                  boxWidth: 10
                                }
                              },
                              tooltip: {
                                callbacks: {
                                  label: function(context) {
                                    const value = context.raw || 0;
                                    const total = context.dataset.data.reduce((a, b) => a + (b || 0), 0);
                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                    const name = aggregateData.byAgency[context.dataIndex]?.name || 'Unknown';
                                    return `${name}: $${value.toLocaleString()} (${percentage}%)`;
                                  }
                                },
                                titleFont: {
                                  size: 14,
                                  weight: 'bold'
                                },
                                bodyFont: {
                                  size: 13,
                                  weight: 'medium'
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-4 text-center text-sm text-gray-400 font-inter">
                      Top 6 agencies by total funding across all electorates
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-gray-750 rounded-lg flex items-center justify-center h-full text-gray-400 font-inter">
                    <div className="text-center">
                      <p className="mb-2">Loading aggregate agency data...</p>
                      <p className="text-sm">This may take a moment to compile data from all electorates.</p>
                      {isLoading && (
                        <div className="mt-4 flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
